<template>
  <transition name="fade">
    <section class="radio-select-section" :class="`radio-select-section--${screenOrientationType}`">
      <div class="radio-select-section__mask" @click="toggleRadioVoice" />
      <div
        class="radio-select-section__content"
        :class="{
          'radio-select-section--has-items': getCurrentSelectedPlayerRadioDataSet.length > 0,
        }"
      >
        <h3 class="radio-select-section__title">{{ $t('RaceVideoPage.RadioSelect.title') }}</h3>
        <button class="radio-select-section__live-button" @click="toggleRadioLive">
          <span class="radio-select-section__live-button__label">LIVE</span>
          <span
            class="radio-select-section__live-button__toggle"
            :class="{ 'radio-select-section__live-button__toggle--active': livePlayerRadioEnabled }"
          ></span>
        </button>
        <recycle-scroller
          class="radio-select-section__radio-list"
          :items="getCurrentSelectedPlayerRadioDataSet"
          :item-size="54"
          key-field="audio_clip"
          v-slot="{ item }"
        >
          <radio-list-item-parts
            :radioData="item"
            :screenOrientationType="screenOrientationType"
            @selectPlayerRadioPlay="playSelectPlayerRadio"
          />
        </recycle-scroller>
      </div>
    </section>
  </transition>
</template>

<script lang="ts">
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { defineComponent, nextTick, watch } from '@vue/composition-api'
import RadioListItemParts from '@/components/RaceVideoPage/common/parts/RadioListItemParts.vue'
import useRadio from '@/components/RaceVideoPage/hook/useRadio'
import RadioDataDocument from '@/store/stores/collectionModule/documents/Radio/RadioDataDocument'

/**
 * レース動画再生画面 無線選択セクションのコンポーネント
 */
export default defineComponent({
  name: 'RadioSelectSection',
  components: {
    RadioListItemParts,
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 選択選手無線データ Live再生フラグ
     */
    livePlayerRadioEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 無線リスト 表示/非表示フラグ
     * recycle-scrollerの高さを調整するために使用する
     */
    radioVoiceEnabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { playRadioDataAudio, getCurrentSelectedPlayerRadioDataSet } = useRadio()

    watch(
      () => props.radioVoiceEnabled,
      () => {
        let minHeight = 0
        nextTick(() => {
          // 無線カードborder-bottomが消えないようにするため1pxだけ高さを調整
          const element = document.querySelector('.vue-recycle-scroller__item-wrapper')
          if (element) {
            minHeight = element.clientHeight + 2
          }
          element?.setAttribute('style', `min-height:${String(minHeight)}px;`)
        })
      },
    )

    return {
      playRadioDataAudio,
      getCurrentSelectedPlayerRadioDataSet,
    }
  },
  methods: {
    /**
     * 無線 ライブ再生トグルボタン
     * @event toggleRadioLive
     */
    toggleRadioLive(): void {
      this.$emit('radioLiveToggle')
    },
    /**
     * 無線音声 ON/OFFボタンが押下されたことを通知する
     * @event toggleRadioVoice
     * @type boolean
     */
    toggleRadioVoice(): void {
      this.$emit('radioVoiceToggle')
    },
    /**
     * 選択選手無線 再生
     * @event playSelectPlayerRadio
     */
    async playSelectPlayerRadio(singleRadio: RadioDataDocument, scene: boolean) {
      await this.playRadioDataAudio(singleRadio, scene)
    },
  },
})
</script>

<style lang="scss" scoped>
.radio-select-section {
  --race-select-height: calc(var(--screen-height) - 56.25vw - 44px);

  font-size: min(3.2vw, calc(var(--screen-height) * 0.0217));
  position: relative;
  width: 100%;
  height: var(--race-select-height);
  padding-top: min(4.2667vw, 1em);
  z-index: 24;
  transition: opacity ease-in-out 0.3s;
  opacity: 1;

  &.fade-enter-active,
  &.fade-leave-active {
    pointer-events: auto;
  }

  &.fade-enter,
  &.fade-leave-to {
    opacity: 0;
    pointer-events: none;

    .radio-select-section__content {
      transform: translate(100%, 0);
    }
  }

  &__mask {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.7);
  }

  &__content {
    position: relative;
    display: flex;
    flex-flow: row wrap;
    align-items: flex-start;
    align-content: flex-start;
    justify-content: space-between;
    gap: 20px 0;
    width: max(calc(var(--race-select-height) * 0.46), 252px);
    margin: 0 0 0 auto;
    padding: 24px 0 24px 24px;
    height: calc(var(--race-select-height) - min(4.2667vw, 1em));
    background: $black20;
    border-radius: 8px 0 0;
    overflow-y: hidden;
    transform: translate(0, 0);
    transition: all ease 0.3s;
  }

  &__title {
    width: 4em;
    font-size: 18px;
    font-family: $fontHeading;
    line-height: 1.33;
    letter-spacing: 0.04em;
    color: #fff;
  }

  &__live-button {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding-right: 12px;

    &__label {
      color: rgba($white, 70%);
      @include fontSetTextSmall(12);
    }

    &__toggle {
      position: relative;
      display: block;
      content: '';
      width: 48px;
      height: 26px;
      background: $gray20;
      border-radius: 13px;
      transition: background-color ease 0.2s;

      &::before {
        position: absolute;
        top: 2px;
        left: 2px;
        display: block;
        content: '';
        width: 22px;
        height: 22px;
        background: #e5e5e5;
        border-radius: 50%;
        transition: all ease 0.2s;
      }

      &--active {
        background: linear-gradient(0deg, rgba(0, 219, 166, 0.5), rgba(0, 219, 166, 0.5)), #fff;

        &::before {
          left: 23px;
          background: $white;
        }
      }
    }
  }

  &__radio-list {
    width: 100%;
    height: 100%;
    padding-bottom: 48px;
  }

  &--landscape-primary,
  &--landscape-secondary {
    --race-select-height: calc(var(--screen-height) - 44px);

    position: absolute;
    top: 44px;
    left: 0;
    right: 0;
    bottom: 0;
    height: var(--race-select-height);
    padding-top: 0;

    .radio-select-section {
      &__mask {
        position: fixed;
        background: rgba(0, 0, 0, 0.4);
      }

      &__content {
        width: 100%;
        height: var(--race-select-height);
      }
    }
  }

  &--has-items {
    ::v-deep .vue-recycle-scroller__item-wrapper {
      border-top: 1px solid rgba(#fff, 10%);
    }
  }
}
</style>
<style lang="scss">
.race-video-ranking-pane {
  .radio-select-section {
    position: absolute;
    top: 44px;
    left: 0;
  }
}
</style>
